import React, { useState, useEffect } from 'react';
import BlogPost from '../components/BlogPost';
import { getAllPostsMetadata, BlogPostMetadata } from '../utils/markdownUtils';
import { ensureRelevantImages } from '../utils/imageUtils';
import SEO from '../components/SEO';
import Newsletter from '../components/Newsletter';

const Blog: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState<BlogPostMetadata[]>([]);

  useEffect(() => {
    const loadPosts = async () => {
      setLoading(true);
      try {
        // Get blog posts metadata (no content) for faster loading
        let postsMetadata = getAllPostsMetadata();
        
        // Filter out template posts
        postsMetadata = postsMetadata.filter(post => post.slug !== 'template');
        
        // Ensure all posts have relevant images
        const postsWithImages = await ensureRelevantImages(postsMetadata);
        
        // Log the posts for debugging
        console.log('Posts with images:', postsWithImages);
        
        setPosts(postsWithImages);
      } catch (error) {
        console.error('Error loading blog posts:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <SEO 
        title="Web Development Blog | David Sheeshka"
        description="Insights, tutorials, and thoughts on web development, cloud computing, and modern software techniques. Learn about React, TypeScript, cloud architecture, and more."
        keywords="web development blog, react tutorials, typescript tips, cloud computing, software development, coding best practices"
        url="https://davidsheeshka.com/blog"
        type="website"
      />
      {/* Hero Section */}
      <div className="text-center mb-24">
        <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text pb-2">
          David's Blog
        </h1>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto">
          Thoughts, ideas, and insights about web development, technology, and design. 
          Join me as I explore the latest trends, share practical tips, and discuss the future of the digital landscape.
        </p>
      </div>
      
      {/* Blog Posts */}
      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : posts.length > 0 ? (
        <div className="grid gap-10">
          {posts.map(post => (
            <div key={post.slug} className="transform transition-all duration-300 hover:scale-[1.01]">
              <BlogPost post={post} isPreview={true} />
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-[#151B2C] rounded-lg shadow-lg">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
          </svg>
          <p className="text-xl text-gray-300">No blog posts found.</p>
          <p className="mt-2 text-gray-400">Check back soon for new content!</p>
        </div>
      )}
      
      {/* Newsletter Section */}
      <div className="mt-20 p-8 bg-gradient-to-r from-[#1E293B] to-[#0F172A] rounded-xl shadow-lg">
        <Newsletter />
      </div>
    </div>
  );
};

export default Blog;